import React from 'react';
import { Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { getFileIcon } from './chatFileIcons';
import { FileInfo } from '../../services/fileService';
import { cn } from '../../lib/utils';

interface ChatFileRowProps {
  file: FileInfo;
  selected: boolean;
  dateLocale: string;
  onToggle: (file: FileInfo) => void;
}

const extensionOf = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
};

/** One selectable file in the chat's context picker. */
const ChatFileRow: React.FC<ChatFileRowProps> = ({ file, selected, dateLocale, onToggle }) => {
  const extension = extensionOf(file.name);
  const meta = [
    file.category,
    file.tags?.length ? file.tags.slice(0, 2).join(', ') : null,
    file.uploadedAt
      ? new Date(file.uploadedAt).toLocaleDateString(dateLocale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : null,
  ].filter(Boolean);

  return (
    <li>
      <button
        type="button"
        onClick={() => onToggle(file)}
        aria-pressed={selected}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors',
          'hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
          selected && 'bg-primary/8',
        )}
      >
        {/* Drawn, not a Checkbox: Radix renders one as a <button>, and nesting
            that inside the row button is invalid HTML. The row is the control
            and aria-pressed carries the state, so this is decoration. */}
        <span
          aria-hidden
          className={cn(
            'grid size-4 shrink-0 place-content-center rounded-[4px] border border-primary transition-colors',
            selected && 'bg-primary text-primary-foreground',
          )}
        >
          {selected && <Check className="size-3" strokeWidth={3} />}
        </span>

        <span className="shrink-0 text-muted-foreground">{getFileIcon(file.type)}</span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-[0.8125rem] font-medium">{file.title || file.name}</span>
            {extension && (
              <Badge variant="secondary" className="h-4 shrink-0 px-1 text-[0.625rem] font-semibold">
                {extension}
              </Badge>
            )}
          </span>
          {meta.length > 0 && (
            <span className="mt-0.5 block truncate text-[0.6875rem] text-muted-foreground">
              {meta.join(' · ')}
            </span>
          )}
        </span>
      </button>
    </li>
  );
};

export default ChatFileRow;
